import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { createDispatchers } from "@utils/dispatcher";
import { RootState } from "..";
import { QuestionResponsesSearchFilter } from "@models/Filter";
import type { RollingAverageConfig } from "@/utils/rolling-average-utils";
import {
  type PointInTimeViewerOptions,
  DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
} from "@models/Dataset";
import {
  DEFAULT_VISUALIZATION_BASE,
  type VisualizationBase,
} from "@models/VisualizationBase";
import type { WidgetRollup } from "@models/Rollup";
import { COLOR_CODING_NONE } from "@models/ColorCoding";

// Nested rollup store: outerKey = questionId, innerKey = focusType ("variable" | "value") or "default" for non-grid questions.
export type CategoryRollupsByContext = Record<string, Record<string, WidgetRollup[]>>;

// Persisted focus rollup state, keyed the same way as CategoryRollupsByContext.
export interface FocusRollupState {
  focusMetric?: "single" | "rollup";
  rollupName?: string;
  rollup?: WidgetRollup;
}
export type FocusRollupByContext = Record<string, Record<string, FocusRollupState>>;

// Question selection types
export interface QuestionSelection {
  questionId: string;
  questionLabel: string;
  questionTitle: string;
  questionType: "Single" | "Multiple" | "Float" | "Number";
  variableCount: number;
  responseCount: number;
}

// Focus type for Single questions with multiple variables
export type FocusType = "variable" | "value";

export interface FocusSelection {
  focusType: FocusType;
  selectedVariableId?: string; // For variable focus
  selectedValueId?: string; // For value focus
  selectedVariableLabel?: string; // Store the actual label for API requests
  selectedValueValue?: string | number; // Store the actual value for API requests
  // Focus rollup support
  focusMetric?: "single" | "rollup";
  rollupName?: string;
  rollup?: WidgetRollup;
}

// Weighting options
export type WeightOption = "default" | "none" | "custom" | "per-segment";

export interface CustomWeight {
  questionId: string;
  variableId: string;
  questionLabel: string;
  variableLabel: string;
}

// Time Period Selection (PRD Module 1)
export type TimePeriod =
  | "all_time"
  | "latest_month"
  | "previous_month"
  | "latest_quarter"
  | "previous_quarter"
  | "latest_semester"
  | "previous_semester"
  | "custom_period";

export interface TimePeriodSelection {
  timePeriod: TimePeriod;
  startDate: string | null; // mm/dd/yyyy format
  endDate: string | null; // mm/dd/yyyy format
}

// Statistical Testing
export type StatTestType = "z-test" | "t-test";
export type SignificanceLevel = "90%" | "95%" | "99%";

export interface StatisticalTestingConfig {
  enabled: boolean;
  testType: StatTestType;
  significanceLevel: SignificanceLevel;
  minimumSampleSize: number;
  compareToTotal: boolean; // Compare to total row
  referenceColumnEntity: string | null; // null for "Overall Total", crossVariableId for "Reference Column"
}

// Sorting Options
export type SortingOption = "original" | "manual";

export interface CrosstabConfig {
  // Chart type identifier
  chartType: "crosstab";

  // Dataset selection
  dashboardId: string | null;
  datasetId: string | null;
  datasetName: string | null;
  datasetVersionId: number | null;
  updateMode: "locked" | "auto-update";

  // Question selection
  rowQuestion: QuestionSelection | null;
  columnQuestion: QuestionSelection | null;

  // Focus selection for Single questions with multiple variables
  rowFocus: FocusSelection | null;
  columnFocus: FocusSelection | null;

  // Chart basic settings
  title: string;
  subtitle: string;
  titleBold: boolean;
  titleUnderline: boolean;

  // Display options
  percentageFormatting: number; // 0, 1, or 2 decimal places
  showN: boolean; // Show N values for each column
  minimumBaseSize: number; // Cell suppression threshold (default 0)

  // Weighting
  weightOption: WeightOption;
  customWeight: CustomWeight | null;
  perSegmentWeights: Record<
    string,
    {
      surveyId: string;
      configurationId: string;
      datasetElementQuestionId: string;
      datasetElementVariableId: string;
      weightVariableLabel: string;
      crossColumnVariableId?: string; // Column variable ID for API requests
    }
  >;

  // Time Period Selection (PRD Module 1)
  timePeriodSelection: TimePeriodSelection;

  // Rolling Average (PRD-26)
  rollingAverage: RollingAverageConfig;
  effectiveRollingPeriodCount?: number;
  rollingPeriodsWarningMessage?: string;
  comparisonRollingLabel?: string;
  missingRollingPeriodLabels?: string[];

  // Date range constraints from API
  responsesMinDate?: string; // ISO date string (YYYY-MM-DD)
  responsesMaxDate?: string; // ISO date string (YYYY-MM-DD)

  // Statistical Testing (PRD Module 2)
  statisticalTesting: StatisticalTestingConfig;

  // Viewer Time Options
  viewerOptions: PointInTimeViewerOptions;

  // Visualization Base (researcher-only) — restricts the eligible respondent pool.
  // Optional so older saved configs without it default to "All eligible respondents".
  visualizationBase?: VisualizationBase;

  customRowLabels: Record<string, string>; // variableId/valueId -> custom label
  customColumnLabels: Record<string, string>; // variableId/valueId -> custom label
  hiddenRows: string[]; // variableIds or valueIds to hide
  hiddenColumns: string[]; // variableIds or valueIds to hide
  rowOrder: string[]; // Row IDs in display order (for drag-and-drop reordering)
  columnOrder: string[]; // Column IDs in display order (for drag-and-drop reordering)
  originalRowOrder: string[]; // Original row order (for resetting to original)
  originalColumnOrder: string[]; // Original column order (for resetting to original)
  sortingOption: SortingOption; // Sorting option for rows and columns

  // Color Coding — id of the selected template, or "none" (default). Fixed
  // templates are defined in `@_constants/color-coding-templates`.
  colorCodingTemplateId: string;

  // Row rollups — active flat array for the current question/focus context (sent to /search).
  categoryRollups: WidgetRollup[];
  // Persisted rollups keyed by [questionId][focusType|"default"] so switching contexts restores prior rollups.
  categoryRollupsByContext: CategoryRollupsByContext;
  // Persisted focus rollup state keyed by [questionId][focusType] — mirrors categoryRollupsByContext pattern.
  focusRollupByContext: FocusRollupByContext;
  // Accumulated map of variableId → variableLabel for stable rollup source identifier resolution.
  variableIdLabelMap: Record<string, string>;

  // Column rollups — active flat array for the current column question/focus context (sent to /search as crossCategoryRollups).
  crossCategoryRollups: WidgetRollup[];
  // Persisted column rollups keyed by [columnQuestionId][columnFocusType|"default"] — mirrors categoryRollupsByContext.
  crossCategoryRollupsByContext: CategoryRollupsByContext;
  // Persisted column focus rollup state keyed by [columnQuestionId][columnFocusType] — mirrors focusRollupByContext.
  crossFocusRollupByContext: FocusRollupByContext;
  // Accumulated map of crossVariableId → crossVariableLabel for stable column rollup source identifier resolution.
  crossVariableIdLabelMap: Record<string, string>;

  // Crosstab data structure (calculated from backend)
  data: {
    rows: Array<{
      id: string; // variableId or valueId
      label: string;
      type: "variable" | "value";
    }>;
    columns: Array<{
      id: string; // variableId or valueId
      label: string;
      type: "variable" | "value";
      n: number; // Base N for this column
    }>;
    cells: Record<
      string,
      {
        // key: "rowId_columnId"
        value: string | number | null; // Mean or percentage (string to preserve decimal formatting like "40.00")
        n: number; // Base N for this cell
        suppressed: boolean; // True if below minimum base size
        statSignificance?: number; // Statistical significance for time period comparison
        rowStatSignificance?: number; // Statistical significance for row comparison to total
        refColumnStatSignificance?: 1 | 0 | -1; // Statistical significance for reference column comparison: 1 = significant positive, 0 = not significant, -1 = significant negative
      }
    >;
    nValues?: Record<string, number | string>; // N values for each column (for Show N feature)
    hasSuppressedCells?: boolean; // Whether any cells are suppressed
  };

  // Original data (preserved for reset functionality)
  originalData: CrosstabConfig["data"];
}

interface Questions extends Record<string, any> {
  id: string;
  label: string;
  name: string;
  type: string;
  responseCount?: number;
  responses?: number;
  variables?: Array<{
    id: string;
    label: string;
    values?: Array<{
      id: string;
      title: string;
    }>;
  }>;
}

export interface CrosstabConfigState {
  currentConfig: CrosstabConfig;
  savedConfigs: CrosstabConfig[];
  isDrawerOpen: boolean;
  previewMode: boolean;
  question: Questions | null;
  editingWidgetId: string | null;
  // Store active filters for refetching data with filters
  activeFilters: QuestionResponsesSearchFilter[];
  // Toggle to apply/ignore filters in preview
  applyFilters: boolean;
}

const defaultCrosstabConfig: CrosstabConfig = {
  chartType: "crosstab",
  dashboardId: null,
  datasetId: null,
  datasetName: null,
  datasetVersionId: null,
  updateMode: "locked",
  rowQuestion: null,
  columnQuestion: null,
  rowFocus: null,
  columnFocus: null,
  title: "",
  subtitle: "",
  titleBold: false,
  titleUnderline: false,
  percentageFormatting: 0,
  showN: false,
  minimumBaseSize: 0,
  weightOption: "default",
  customWeight: null,
  perSegmentWeights: {},
  timePeriodSelection: {
    timePeriod: "all_time",
    startDate: null,
    endDate: null,
  },
  rollingAverage: {
    enabled: false,
    windowSize: 2,
  },
  statisticalTesting: {
    enabled: false,
    testType: "t-test",
    significanceLevel: "90%",
    minimumSampleSize: 30,
    compareToTotal: false,
    referenceColumnEntity: null, // null = "Overall Total", crossVariableId = "Reference Column"
  },
  viewerOptions: DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS,
  visualizationBase: { ...DEFAULT_VISUALIZATION_BASE },
  customRowLabels: {},
  customColumnLabels: {},
  hiddenRows: [],
  hiddenColumns: [],
  rowOrder: [],
  columnOrder: [],
  originalRowOrder: [],
  originalColumnOrder: [],
  data: {
    rows: [],
    columns: [],
    cells: {},
  },
  sortingOption: "original",
  colorCodingTemplateId: COLOR_CODING_NONE,
  categoryRollups: [],
  categoryRollupsByContext: {},
  focusRollupByContext: {},
  variableIdLabelMap: {},
  crossCategoryRollups: [],
  crossCategoryRollupsByContext: {},
  crossFocusRollupByContext: {},
  crossVariableIdLabelMap: {},
  originalData: {
    rows: [],
    columns: [],
    cells: {},
  },
};

export const initialState: CrosstabConfigState = {
  currentConfig: { ...defaultCrosstabConfig },
  savedConfigs: [],
  isDrawerOpen: false,
  previewMode: true,
  question: null,
  editingWidgetId: null,
  activeFilters: [],
  applyFilters: true,
};

export const crosstabConfigSlice = createSlice({
  name: "crosstabConfig",
  initialState,
  reducers: {
    openCrosstabDrawer: (state, action: PayloadAction<string>) => {
      state.isDrawerOpen = true;
      state.editingWidgetId = action.payload;
    },
    closeCrosstabDrawer: (state) => {
      state.isDrawerOpen = false;
      state.editingWidgetId = null;
    },
    togglePreviewMode: (state) => {
      state.previewMode = !state.previewMode;
    },

    // Dataset selection
    updateDatasetSelection: (
      state,
      action: PayloadAction<{
        dashboardId?: string;
        datasetId: string;
        datasetName?: string;
        datasetVersionId: number | null;
        updateMode: "locked" | "auto-update";
        activeFilters?: QuestionResponsesSearchFilter[];
      }>,
    ) => {
      state.currentConfig.dashboardId = action.payload.dashboardId ?? null;
      state.currentConfig.datasetId = action.payload.datasetId;
      state.currentConfig.datasetName = action.payload.datasetName || null;
      state.currentConfig.datasetVersionId = action.payload.datasetVersionId;
      state.currentConfig.updateMode = action.payload.updateMode;
      state.activeFilters = action.payload.activeFilters ?? [];
      // Reset questions when dataset changes
      state.currentConfig.rowQuestion = null;
      state.currentConfig.columnQuestion = null;
      state.currentConfig.rowFocus = null;
      state.currentConfig.columnFocus = null;
    },

    // Question selection
    updateRowQuestion: (
      state,
      action: PayloadAction<QuestionSelection | null>,
    ) => {
      // Persist current rollups and focus rollup under the outgoing question+focus key before switching.
      const outgoingQuestionId = state.currentConfig.rowQuestion?.questionId;
      const outgoingFocusKey = state.currentConfig.rowFocus?.focusType ?? "default";
      if (outgoingQuestionId) {
        if (!state.currentConfig.categoryRollupsByContext[outgoingQuestionId]) {
          state.currentConfig.categoryRollupsByContext[outgoingQuestionId] = {};
        }
        state.currentConfig.categoryRollupsByContext[outgoingQuestionId][outgoingFocusKey] =
          [...state.currentConfig.categoryRollups];

        if (!state.currentConfig.focusRollupByContext[outgoingQuestionId]) {
          state.currentConfig.focusRollupByContext[outgoingQuestionId] = {};
        }
        state.currentConfig.focusRollupByContext[outgoingQuestionId][outgoingFocusKey] = {
          focusMetric: state.currentConfig.rowFocus?.focusMetric,
          rollupName: state.currentConfig.rowFocus?.rollupName,
          rollup: state.currentConfig.rowFocus?.rollup,
        };
      }

      state.currentConfig.rowQuestion = action.payload;

      // Initialize focus with default "variable" type if question needs focus
      let incomingFocusKey: string;
      if (
        action.payload?.questionType === "Single" &&
        action.payload.variableCount > 1
      ) {
        state.currentConfig.rowFocus = {
          focusType: "variable",
          selectedVariableId: undefined,
          selectedValueId: undefined,
        };
        incomingFocusKey = "variable";
      } else {
        state.currentConfig.rowFocus = null;
        incomingFocusKey = "default";
      }

      // Restore rollups for the incoming question+focus, or empty for Numeric/Float.
      const incomingQuestionId = action.payload?.questionId;
      const isNumericOrFloat =
        action.payload?.questionType === "Float" ||
        action.payload?.questionType === "Number";
      state.currentConfig.categoryRollups =
        !isNumericOrFloat && incomingQuestionId
          ? (state.currentConfig.categoryRollupsByContext[incomingQuestionId]?.[
              incomingFocusKey
            ] ?? [])
          : [];

      // Restore focus rollup state for the incoming question+focus.
      if (!isNumericOrFloat && incomingQuestionId && state.currentConfig.rowFocus) {
        const savedFocusRollup =
          state.currentConfig.focusRollupByContext[incomingQuestionId]?.[incomingFocusKey];
        if (savedFocusRollup) {
          state.currentConfig.rowFocus.focusMetric = savedFocusRollup.focusMetric;
          state.currentConfig.rowFocus.rollupName = savedFocusRollup.rollupName;
          state.currentConfig.rowFocus.rollup = savedFocusRollup.rollup;
        }
      }

      // Reset per-segment weight to default when question changes
      if (state.currentConfig.weightOption === "per-segment") {
        state.currentConfig.perSegmentWeights = {};
        state.currentConfig.weightOption = "default";
      }

      // Clear stale row/column customization so new data renders correctly
      state.currentConfig.rowOrder = [];
      state.currentConfig.columnOrder = [];
      state.currentConfig.originalRowOrder = [];
      state.currentConfig.originalColumnOrder = [];
      state.currentConfig.hiddenRows = [];
      state.currentConfig.hiddenColumns = [];
      state.currentConfig.customRowLabels = {};
      state.currentConfig.customColumnLabels = {};

      // Auto-generate title if both questions are selected
      if (action.payload && state.currentConfig.columnQuestion) {
        state.currentConfig.title = `Crosstab ${action.payload.questionTitle} by ${state.currentConfig.columnQuestion.questionTitle}`;
      }
    },

    updateColumnQuestion: (
      state,
      action: PayloadAction<QuestionSelection | null>,
    ) => {
      // Persist current column rollups under the outgoing question+focus key before switching.
      const outgoingQuestionId = state.currentConfig.columnQuestion?.questionId;
      const outgoingFocusKey =
        state.currentConfig.columnFocus?.focusType ?? "default";
      if (outgoingQuestionId) {
        if (!state.currentConfig.crossCategoryRollupsByContext[outgoingQuestionId]) {
          state.currentConfig.crossCategoryRollupsByContext[outgoingQuestionId] = {};
        }
        state.currentConfig.crossCategoryRollupsByContext[outgoingQuestionId][
          outgoingFocusKey
        ] = [...state.currentConfig.crossCategoryRollups];

        if (!state.currentConfig.crossFocusRollupByContext[outgoingQuestionId]) {
          state.currentConfig.crossFocusRollupByContext[outgoingQuestionId] = {};
        }
        state.currentConfig.crossFocusRollupByContext[outgoingQuestionId][
          outgoingFocusKey
        ] = {
          focusMetric: state.currentConfig.columnFocus?.focusMetric,
          rollupName: state.currentConfig.columnFocus?.rollupName,
          rollup: state.currentConfig.columnFocus?.rollup,
        };
      }

      state.currentConfig.columnQuestion = action.payload;

      // Initialize focus with default "variable" type if question needs focus
      let incomingFocusKey: string;
      if (
        action.payload?.questionType === "Single" &&
        action.payload.variableCount > 1
      ) {
        state.currentConfig.columnFocus = {
          focusType: "variable",
          selectedVariableId: undefined,
          selectedValueId: undefined,
        };
        incomingFocusKey = "variable";
      } else {
        state.currentConfig.columnFocus = null;
        incomingFocusKey = "default";
      }

      // Restore column rollups for the incoming question+focus.
      const incomingQuestionId = action.payload?.questionId;
      state.currentConfig.crossCategoryRollups = incomingQuestionId
        ? (state.currentConfig.crossCategoryRollupsByContext[incomingQuestionId]?.[
            incomingFocusKey
          ] ?? [])
        : [];

      // Restore column focus rollup state for the incoming question+focus.
      if (incomingQuestionId && state.currentConfig.columnFocus) {
        const savedFocusRollup =
          state.currentConfig.crossFocusRollupByContext[incomingQuestionId]?.[
            incomingFocusKey
          ];
        if (savedFocusRollup) {
          state.currentConfig.columnFocus.focusMetric =
            savedFocusRollup.focusMetric;
          state.currentConfig.columnFocus.rollupName =
            savedFocusRollup.rollupName;
          state.currentConfig.columnFocus.rollup = savedFocusRollup.rollup;
        }
      }

      // Clear reference column when column question changes (PRD Module 2)
      state.currentConfig.statisticalTesting.referenceColumnEntity = null;

      // Reset per-segment weight to default when question changes
      if (state.currentConfig.weightOption === "per-segment") {
        state.currentConfig.perSegmentWeights = {};
        state.currentConfig.weightOption = "default";
      }

      // Clear stale row/column customization so new data renders correctly
      state.currentConfig.rowOrder = [];
      state.currentConfig.columnOrder = [];
      state.currentConfig.originalRowOrder = [];
      state.currentConfig.originalColumnOrder = [];
      state.currentConfig.hiddenRows = [];
      state.currentConfig.hiddenColumns = [];
      state.currentConfig.customRowLabels = {};
      state.currentConfig.customColumnLabels = {};

      // Auto-generate title if both questions are selected
      if (action.payload && state.currentConfig.rowQuestion) {
        state.currentConfig.title = `Crosstab ${state.currentConfig.rowQuestion.questionTitle} by ${action.payload.questionTitle}`;
      }
    },

    // Focus selection
    updateRowFocus: (state, action: PayloadAction<FocusSelection | null>) => {
      const questionId = state.currentConfig.rowQuestion?.questionId;
      const oldFocusKey = state.currentConfig.rowFocus?.focusType ?? "default";
      const newFocusKey = action.payload?.focusType ?? "default";

      if (questionId && oldFocusKey !== newFocusKey) {
        // Persist category rollups under the outgoing focus key.
        if (!state.currentConfig.categoryRollupsByContext[questionId]) {
          state.currentConfig.categoryRollupsByContext[questionId] = {};
        }
        state.currentConfig.categoryRollupsByContext[questionId][oldFocusKey] =
          [...state.currentConfig.categoryRollups];
        // Restore category rollups for the incoming focus key.
        state.currentConfig.categoryRollups =
          state.currentConfig.categoryRollupsByContext[questionId]?.[newFocusKey] ?? [];

        // Persist focus rollup state under the outgoing focus key.
        if (!state.currentConfig.focusRollupByContext[questionId]) {
          state.currentConfig.focusRollupByContext[questionId] = {};
        }
        state.currentConfig.focusRollupByContext[questionId][oldFocusKey] = {
          focusMetric: state.currentConfig.rowFocus?.focusMetric,
          rollupName: state.currentConfig.rowFocus?.rollupName,
          rollup: state.currentConfig.rowFocus?.rollup,
        };
      }

      state.currentConfig.rowFocus = action.payload;

      // Restore focus rollup state for the incoming focus type (applied after setting new focus).
      if (questionId && oldFocusKey !== newFocusKey && state.currentConfig.rowFocus) {
        const savedFocusRollup =
          state.currentConfig.focusRollupByContext[questionId]?.[newFocusKey];
        if (savedFocusRollup) {
          state.currentConfig.rowFocus.focusMetric = savedFocusRollup.focusMetric;
          state.currentConfig.rowFocus.rollupName = savedFocusRollup.rollupName;
          state.currentConfig.rowFocus.rollup = savedFocusRollup.rollup;
        }
      }
    },

    updateColumnFocus: (
      state,
      action: PayloadAction<FocusSelection | null>,
    ) => {
      const questionId = state.currentConfig.columnQuestion?.questionId;
      const oldFocusKey = state.currentConfig.columnFocus?.focusType ?? "default";
      const newFocusKey = action.payload?.focusType ?? "default";

      if (questionId && oldFocusKey !== newFocusKey) {
        // Persist column rollups under the outgoing focus key.
        if (!state.currentConfig.crossCategoryRollupsByContext[questionId]) {
          state.currentConfig.crossCategoryRollupsByContext[questionId] = {};
        }
        state.currentConfig.crossCategoryRollupsByContext[questionId][oldFocusKey] =
          [...state.currentConfig.crossCategoryRollups];
        // Restore column rollups for the incoming focus key.
        state.currentConfig.crossCategoryRollups =
          state.currentConfig.crossCategoryRollupsByContext[questionId]?.[
            newFocusKey
          ] ?? [];

        // Persist column focus rollup state under the outgoing focus key.
        if (!state.currentConfig.crossFocusRollupByContext[questionId]) {
          state.currentConfig.crossFocusRollupByContext[questionId] = {};
        }
        state.currentConfig.crossFocusRollupByContext[questionId][oldFocusKey] = {
          focusMetric: state.currentConfig.columnFocus?.focusMetric,
          rollupName: state.currentConfig.columnFocus?.rollupName,
          rollup: state.currentConfig.columnFocus?.rollup,
        };
      }

      state.currentConfig.columnFocus = action.payload;

      // Restore column focus rollup state for the incoming focus type.
      if (questionId && oldFocusKey !== newFocusKey && state.currentConfig.columnFocus) {
        const savedFocusRollup =
          state.currentConfig.crossFocusRollupByContext[questionId]?.[newFocusKey];
        if (savedFocusRollup) {
          state.currentConfig.columnFocus.focusMetric = savedFocusRollup.focusMetric;
          state.currentConfig.columnFocus.rollupName = savedFocusRollup.rollupName;
          state.currentConfig.columnFocus.rollup = savedFocusRollup.rollup;
        }
      }
    },

    // Title and subtitle
    updateChartTitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.title = action.payload;
    },
    updateChartSubtitle: (state, action: PayloadAction<string>) => {
      state.currentConfig.subtitle = action.payload;
    },
    updateTitleBold: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleBold = action.payload;
    },
    updateTitleUnderline: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.titleUnderline = action.payload;
    },

    // Display options
    updatePercentageFormatting: (state, action: PayloadAction<number>) => {
      state.currentConfig.percentageFormatting = action.payload;
    },
    updateShowN: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.showN = action.payload;
    },
    updateMinimumBaseSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.minimumBaseSize = action.payload;
    },

    // Weighting
    updateWeightOption: (state, action: PayloadAction<WeightOption>) => {
      state.currentConfig.weightOption = action.payload;
      if (action.payload !== "custom") {
        state.currentConfig.customWeight = null;
      }
      if (action.payload !== "per-segment") {
        state.currentConfig.perSegmentWeights = {};
      } else {
        // Initialize perSegmentWeights if it doesn't exist
        if (!state.currentConfig.perSegmentWeights) {
          state.currentConfig.perSegmentWeights = {};
        }
      }
    },
    updateCustomWeight: (state, action: PayloadAction<CustomWeight | null>) => {
      state.currentConfig.customWeight = action.payload;
      if (action.payload) {
        state.currentConfig.weightOption = "custom";
      }
    },
    updatePerSegmentWeights: (
      state,
      action: PayloadAction<
        Record<
          string,
          {
            surveyId: string;
            configurationId: string;
            datasetElementQuestionId: string;
            datasetElementVariableId: string;
            weightVariableLabel: string;
            crossColumnVariableId?: string; // Column variable ID for API requests
          }
        >
      >,
    ) => {
      state.currentConfig.perSegmentWeights = action.payload;
    },

    // Time Period Selection actions
    updateTimePeriod: (state, action: PayloadAction<TimePeriod>) => {
      state.currentConfig.timePeriodSelection.timePeriod = action.payload;
      // Reset dates when changing time period
      if (action.payload !== "custom_period") {
        state.currentConfig.timePeriodSelection.startDate = null;
        state.currentConfig.timePeriodSelection.endDate = null;
      }
      // Clear stale rolling average response data so footer reflects new windowSize immediately
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },
    updateTimePeriodDates: (
      state,
      action: PayloadAction<{
        startDate: string | null;
        endDate: string | null;
      }>,
    ) => {
      state.currentConfig.timePeriodSelection.startDate =
        action.payload.startDate;
      state.currentConfig.timePeriodSelection.endDate = action.payload.endDate;
    },

    // Rolling Average actions
    updateRollingAverageEnabled: (state, action: PayloadAction<boolean>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.enabled = action.payload;
      if (!action.payload) {
        state.currentConfig.rollingAverage.windowSize = 2;
      }
    },
    updateRollingAverageWindowSize: (state, action: PayloadAction<number>) => {
      if (!state.currentConfig.rollingAverage) {
        state.currentConfig.rollingAverage = { enabled: false, windowSize: 2 };
      }
      state.currentConfig.rollingAverage.windowSize = action.payload;
      // Clear stale API response data so footer reflects new windowSize immediately
      state.currentConfig.effectiveRollingPeriodCount = undefined;
      state.currentConfig.comparisonRollingLabel = undefined;
      state.currentConfig.rollingPeriodsWarningMessage = undefined;
      state.currentConfig.missingRollingPeriodLabels = undefined;
    },

    // Viewer Options actions
    updateViewerOptions: (
      state,
      action: PayloadAction<PointInTimeViewerOptions>,
    ) => {
      state.currentConfig.viewerOptions = action.payload;
    },

    updateVisualizationBase: (
      state,
      action: PayloadAction<VisualizationBase>,
    ) => {
      state.currentConfig.visualizationBase = action.payload;
    },

    // Statistical Testing actions
    updateStatTestingEnabled: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.enabled = action.payload;
      // Disable compare to total if stat testing is disabled
      if (!action.payload) {
        state.currentConfig.statisticalTesting.compareToTotal = false;
      }
    },
    updateStatTestType: (state, action: PayloadAction<StatTestType>) => {
      state.currentConfig.statisticalTesting.testType = action.payload;
    },
    updateSignificanceLevel: (
      state,
      action: PayloadAction<SignificanceLevel>,
    ) => {
      state.currentConfig.statisticalTesting.significanceLevel = action.payload;
    },
    updateMinimumSampleSize: (state, action: PayloadAction<number>) => {
      state.currentConfig.statisticalTesting.minimumSampleSize = action.payload;
    },
    updateCompareToTotal: (state, action: PayloadAction<boolean>) => {
      state.currentConfig.statisticalTesting.compareToTotal = action.payload;
      // Reset reference settings when disabling compare to total
      if (!action.payload) {
        state.currentConfig.statisticalTesting.referenceColumnEntity = null;
      }
    },
    updateReferenceColumnEntity: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      // null = "Overall Total", crossVariableId (string) = "Reference Column"
      state.currentConfig.statisticalTesting.referenceColumnEntity =
        action.payload;
    },

    // Custom labels
    updateCustomRowLabel: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      state.currentConfig.customRowLabels[action.payload.id] =
        action.payload.label;
    },
    updateCustomColumnLabel: (
      state,
      action: PayloadAction<{ id: string; label: string }>,
    ) => {
      state.currentConfig.customColumnLabels[action.payload.id] =
        action.payload.label;
    },
    resetCustomRowLabel: (state, action: PayloadAction<string>) => {
      delete state.currentConfig.customRowLabels[action.payload];
    },
    resetCustomColumnLabel: (state, action: PayloadAction<string>) => {
      delete state.currentConfig.customColumnLabels[action.payload];
    },

    // Row and column visibility
    toggleRowVisibility: (state, action: PayloadAction<string>) => {
      const rowId = action.payload;
      const index = state.currentConfig.hiddenRows.indexOf(rowId);

      if (index > -1) {
        state.currentConfig.hiddenRows.splice(index, 1);
      } else {
        state.currentConfig.hiddenRows.push(rowId);
      }
    },
    toggleColumnVisibility: (state, action: PayloadAction<string>) => {
      const columnId = action.payload;
      const index = state.currentConfig.hiddenColumns.indexOf(columnId);

      if (index > -1) {
        state.currentConfig.hiddenColumns.splice(index, 1);
      } else {
        state.currentConfig.hiddenColumns.push(columnId);
      }
    },
    showAllRows: (state) => {
      state.currentConfig.hiddenRows = [];
    },
    hideAllRows: (state) => {
      state.currentConfig.hiddenRows = state.currentConfig.data.rows.map(
        (row) => row.id,
      );
    },
    showAllColumns: (state) => {
      state.currentConfig.hiddenColumns = [];
    },
    hideAllColumns: (state) => {
      state.currentConfig.hiddenColumns = state.currentConfig.data.columns.map(
        (col) => col.id,
      );
    },
    reorderRows: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const rowOrder = [...state.currentConfig.rowOrder];
      const [removed] = rowOrder.splice(fromIndex, 1);
      rowOrder.splice(toIndex, 0, removed);
      state.currentConfig.rowOrder = rowOrder;
    },
    reorderColumns: (
      state,
      action: PayloadAction<{ fromIndex: number; toIndex: number }>,
    ) => {
      const { fromIndex, toIndex } = action.payload;
      const columnOrder = [...state.currentConfig.columnOrder];
      const [removed] = columnOrder.splice(fromIndex, 1);
      columnOrder.splice(toIndex, 0, removed);
      state.currentConfig.columnOrder = columnOrder;
    },

    // Sorting option
    updateSortingOption: (state, action: PayloadAction<SortingOption>) => {
      state.currentConfig.sortingOption = action.payload;

      // Reset orders to original if switching to original
      if (action.payload === "original") {
        // Use the stored original order (captured when preview data first loaded)
        if (state.currentConfig.originalRowOrder.length > 0) {
          state.currentConfig.rowOrder = [
            ...state.currentConfig.originalRowOrder,
          ];
        }
        if (state.currentConfig.originalColumnOrder.length > 0) {
          state.currentConfig.columnOrder = [
            ...state.currentConfig.originalColumnOrder,
          ];
        }
      }
    },

    // Color Coding template selection ("none" disables color coding)
    updateColorCodingTemplate: (state, action: PayloadAction<string>) => {
      state.currentConfig.colorCodingTemplateId = action.payload;
    },

    // Row rollup CRUD
    addCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      if (!Array.isArray(state.currentConfig.categoryRollups)) {
        state.currentConfig.categoryRollups = [];
      }
      state.currentConfig.categoryRollups.push(action.payload);
    },
    updateCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      if (!Array.isArray(state.currentConfig.categoryRollups)) return;
      const idx = state.currentConfig.categoryRollups.findIndex(
        (r) => r.id === action.payload.id,
      );
      if (idx > -1) {
        state.currentConfig.categoryRollups[idx] = action.payload;
      }
    },
    deleteCategoryRollup: (state, action: PayloadAction<string>) => {
      state.currentConfig.categoryRollups = (
        state.currentConfig.categoryRollups ?? []
      ).filter((r) => r.id !== action.payload);
    },
    clearCategoryRollups: (state) => {
      state.currentConfig.categoryRollups = [];
    },
    mergeVariableIdLabelMap: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      state.currentConfig.variableIdLabelMap = {
        ...state.currentConfig.variableIdLabelMap,
        ...action.payload,
      };
    },

    // Column rollup CRUD
    addCrossCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      if (!Array.isArray(state.currentConfig.crossCategoryRollups)) {
        state.currentConfig.crossCategoryRollups = [];
      }
      state.currentConfig.crossCategoryRollups.push(action.payload);
    },
    updateCrossCategoryRollup: (state, action: PayloadAction<WidgetRollup>) => {
      if (!Array.isArray(state.currentConfig.crossCategoryRollups)) return;
      const idx = state.currentConfig.crossCategoryRollups.findIndex(
        (r) => r.id === action.payload.id,
      );
      if (idx > -1) {
        state.currentConfig.crossCategoryRollups[idx] = action.payload;
      }
    },
    deleteCrossCategoryRollup: (state, action: PayloadAction<string>) => {
      state.currentConfig.crossCategoryRollups = (
        state.currentConfig.crossCategoryRollups ?? []
      ).filter((r) => r.id !== action.payload);
    },
    clearCrossCategoryRollups: (state) => {
      state.currentConfig.crossCategoryRollups = [];
    },
    mergeCrossVariableIdLabelMap: (
      state,
      action: PayloadAction<Record<string, string>>,
    ) => {
      state.currentConfig.crossVariableIdLabelMap = {
        ...state.currentConfig.crossVariableIdLabelMap,
        ...action.payload,
      };
    },

    // Initialize row/column order from preview data
    initializeRowColumnOrder: (
      state,
      action: PayloadAction<{
        rowIds: string[];
        columnIds: string[];
      }>,
    ) => {
      // Ensure arrays are always initialized to avoid runtime errors
      if (!Array.isArray(state.currentConfig.rowOrder)) {
        state.currentConfig.rowOrder = [];
      }
      if (!Array.isArray(state.currentConfig.columnOrder)) {
        state.currentConfig.columnOrder = [];
      }
      if (!Array.isArray(state.currentConfig.originalRowOrder)) {
        state.currentConfig.originalRowOrder = [];
      }
      if (!Array.isArray(state.currentConfig.originalColumnOrder)) {
        state.currentConfig.originalColumnOrder = [];
      }

      // Check if columnIds have changed (when switching between focus modes)
      const columnsChanged =
        state.currentConfig.columnOrder.length !==
          action.payload.columnIds.length ||
        !action.payload.columnIds.every((id) =>
          state.currentConfig.columnOrder.includes(id),
        );

      // Check if rowIds have changed (e.g. different data returned by API)
      const rowsChanged =
        state.currentConfig.rowOrder.length !== action.payload.rowIds.length ||
        !action.payload.rowIds.every((id) =>
          state.currentConfig.rowOrder.includes(id),
        );

      // Initialize rows if empty OR if rows have changed
      if (state.currentConfig.rowOrder.length === 0 || rowsChanged) {
        state.currentConfig.rowOrder = action.payload.rowIds;
        // Also reset hiddenRows when rows change to avoid hiding non-existent rows
        state.currentConfig.hiddenRows = [];
      }

      // Initialize columns if empty OR if columns have changed (focus mode switch)
      if (state.currentConfig.columnOrder.length === 0 || columnsChanged) {
        state.currentConfig.columnOrder = action.payload.columnIds;
        // Also reset hiddenColumns when columns change to avoid hiding non-existent columns
        state.currentConfig.hiddenColumns = [];
      }

      // Always store the original order for reset functionality
      if (state.currentConfig.originalRowOrder.length === 0 || rowsChanged) {
        state.currentConfig.originalRowOrder = action.payload.rowIds;
      }
      if (
        state.currentConfig.originalColumnOrder.length === 0 ||
        columnsChanged
      ) {
        state.currentConfig.originalColumnOrder = action.payload.columnIds;
      }
    },

    // Data updates
    updateCrosstabData: (
      state,
      action: PayloadAction<
        CrosstabConfig["data"] & {
          responsesMinDate?: string;
          responsesMaxDate?: string;
        }
      >,
    ) => {
      const { responsesMinDate, responsesMaxDate, ...data } = action.payload;
      state.currentConfig.data = data;
      state.currentConfig.originalData = JSON.parse(JSON.stringify(data));

      // Store original order from data for reset functionality
      state.currentConfig.originalRowOrder = data.rows.map((row) => row.id);
      state.currentConfig.originalColumnOrder = data.columns.map(
        (col) => col.id,
      );

      // Reconcile rowOrder: preserve manual order for rows that still exist,
      // append any new rows, and remove rows that no longer exist.
      const newRowIds = new Set(data.rows.map((row) => row.id));
      const existingRowOrder = state.currentConfig.rowOrder;
      if (existingRowOrder.length === 0) {
        // First time — initialize from data
        state.currentConfig.rowOrder = data.rows.map((row) => row.id);
      } else {
        // Keep existing order for rows that still exist
        const preserved = existingRowOrder.filter((id) => newRowIds.has(id));
        // Append any new rows not in the existing order
        const existingIds = new Set(existingRowOrder);
        const added = data.rows
          .map((row) => row.id)
          .filter((id) => !existingIds.has(id));
        state.currentConfig.rowOrder = [...preserved, ...added];
      }

      // Reconcile columnOrder the same way
      const newColIds = new Set(data.columns.map((col) => col.id));
      const existingColOrder = state.currentConfig.columnOrder;
      if (existingColOrder.length === 0) {
        state.currentConfig.columnOrder = data.columns.map((col) => col.id);
      } else {
        const preserved = existingColOrder.filter((id) => newColIds.has(id));
        const existingIds = new Set(existingColOrder);
        const added = data.columns
          .map((col) => col.id)
          .filter((id) => !existingIds.has(id));
        state.currentConfig.columnOrder = [...preserved, ...added];
      }

      // Update date range constraints if provided
      if (responsesMinDate) {
        state.currentConfig.responsesMinDate = responsesMinDate;
      }
      if (responsesMaxDate) {
        state.currentConfig.responsesMaxDate = responsesMaxDate;
      }
    },

    // Update only response date constraints (without touching data/order)
    updateResponseDates: (
      state,
      action: PayloadAction<{
        responsesMinDate?: string;
        responsesMaxDate?: string;
        effectiveRollingPeriodCount?: number;
        rollingPeriodsWarningMessage?: string;
        comparisonRollingLabel?: string;
        missingRollingPeriodLabels?: string[];
      }>,
    ) => {
      if (action.payload.responsesMinDate) {
        state.currentConfig.responsesMinDate = action.payload.responsesMinDate;
      }
      if (action.payload.responsesMaxDate) {
        state.currentConfig.responsesMaxDate = action.payload.responsesMaxDate;
      }
      // Update rolling average response fields
      state.currentConfig.effectiveRollingPeriodCount =
        action.payload.effectiveRollingPeriodCount;
      state.currentConfig.rollingPeriodsWarningMessage =
        action.payload.rollingPeriodsWarningMessage;
      state.currentConfig.comparisonRollingLabel =
        action.payload.comparisonRollingLabel;
      state.currentConfig.missingRollingPeriodLabels =
        action.payload.missingRollingPeriodLabels;
    },

    // Question metadata
    updateQuestions: (state, action: PayloadAction<Questions | null>) => {
      state.question = action.payload;
    },

    // Save and load
    saveCurrentConfig: (state) => {
      const configToSave = JSON.parse(
        JSON.stringify(state.currentConfig),
      ) as CrosstabConfig;
      const tp = configToSave.timePeriodSelection?.timePeriod;
      if (tp && tp !== "all_time" && tp !== "custom_period") {
        configToSave.timePeriodSelection = {
          ...configToSave.timePeriodSelection,
          startDate: null,
          endDate: null,
        };
      }
      state.savedConfigs.push(configToSave);
    },
    loadConfig: (state, action: PayloadAction<CrosstabConfig>) => {
      const config = JSON.parse(JSON.stringify(action.payload));

      // Ensure newer fields are initialized for backwards compatibility
      if (!config.perSegmentWeights) {
        config.perSegmentWeights = {};
      }
      if (!Array.isArray(config.rowOrder)) {
        config.rowOrder = [];
      }
      if (!Array.isArray(config.columnOrder)) {
        config.columnOrder = [];
      }
      if (!Array.isArray(config.originalRowOrder)) {
        config.originalRowOrder = [];
      }
      if (!Array.isArray(config.originalColumnOrder)) {
        config.originalColumnOrder = [];
      }
      if (!Array.isArray(config.hiddenRows)) {
        config.hiddenRows = [];
      }
      if (!Array.isArray(config.hiddenColumns)) {
        config.hiddenColumns = [];
      }
      if (!config.customRowLabels) {
        config.customRowLabels = {};
      }
      if (!config.customColumnLabels) {
        config.customColumnLabels = {};
      }
      if (!config.rollingAverage) {
        config.rollingAverage = { enabled: false, windowSize: 2 };
      }
      if (!config.statisticalTesting) {
        config.statisticalTesting = { enabled: false };
      }
      if (!config.timePeriodSelection) {
        config.timePeriodSelection = {
          timePeriod: "all_time",
          startDate: null,
          endDate: null,
        };
      }
      if (!config.viewerOptions) {
        config.viewerOptions = DEFAULT_POINT_IN_TIME_VIEWER_OPTIONS;
      }
      if (!config.visualizationBase) {
        config.visualizationBase = { ...DEFAULT_VISUALIZATION_BASE };
      }
      if (!Array.isArray(config.categoryRollups)) {
        config.categoryRollups = [];
      }
      if (
        !config.categoryRollupsByContext ||
        typeof config.categoryRollupsByContext !== "object" ||
        Array.isArray(config.categoryRollupsByContext)
      ) {
        config.categoryRollupsByContext = {};
      }
      if (
        !config.focusRollupByContext ||
        typeof config.focusRollupByContext !== "object" ||
        Array.isArray(config.focusRollupByContext)
      ) {
        config.focusRollupByContext = {};
      }
      if (
        !config.variableIdLabelMap ||
        typeof config.variableIdLabelMap !== "object" ||
        Array.isArray(config.variableIdLabelMap)
      ) {
        config.variableIdLabelMap = {};
      }
      if (!Array.isArray(config.crossCategoryRollups)) {
        config.crossCategoryRollups = [];
      }
      if (
        !config.crossCategoryRollupsByContext ||
        typeof config.crossCategoryRollupsByContext !== "object" ||
        Array.isArray(config.crossCategoryRollupsByContext)
      ) {
        config.crossCategoryRollupsByContext = {};
      }
      if (
        !config.crossFocusRollupByContext ||
        typeof config.crossFocusRollupByContext !== "object" ||
        Array.isArray(config.crossFocusRollupByContext)
      ) {
        config.crossFocusRollupByContext = {};
      }
      if (
        !config.crossVariableIdLabelMap ||
        typeof config.crossVariableIdLabelMap !== "object" ||
        Array.isArray(config.crossVariableIdLabelMap)
      ) {
        config.crossVariableIdLabelMap = {};
      }
      // timePeriodSelection is left as-is; the handler hooks re-resolve dates
      // using the dataset availability cutoff once the drawer opens.

      state.currentConfig = config;
    },
    resetToDefault: (state) => {
      const originalData = state.currentConfig.originalData;
      state.currentConfig = {
        ...defaultCrosstabConfig,
        originalData,
        data: JSON.parse(JSON.stringify(originalData)),
      };
    },

    // Filter toggle actions
    toggleApplyFilters: (state) => {
      state.applyFilters = !state.applyFilters;
    },
    setApplyFilters: (state, action: PayloadAction<boolean>) => {
      state.applyFilters = action.payload;
    },
  },
});

export const {
  openCrosstabDrawer,
  closeCrosstabDrawer,
  togglePreviewMode,
  updateDatasetSelection,
  updateRowQuestion,
  updateColumnQuestion,
  updateRowFocus,
  updateColumnFocus,
  updateChartTitle,
  updateChartSubtitle,
  updateTitleBold,
  updateTitleUnderline,
  updatePercentageFormatting,
  updateShowN,
  updateMinimumBaseSize,
  updateWeightOption,
  updateCustomWeight,
  updatePerSegmentWeights,
  updateCustomRowLabel,
  updateCustomColumnLabel,
  resetCustomRowLabel,
  resetCustomColumnLabel,
  toggleRowVisibility,
  toggleColumnVisibility,
  showAllRows,
  hideAllRows,
  showAllColumns,
  hideAllColumns,
  reorderRows,
  reorderColumns,
  initializeRowColumnOrder,
  updateCrosstabData,
  updateResponseDates,
  updateQuestions,
  saveCurrentConfig,
  loadConfig,
  resetToDefault,
  updateTimePeriod,
  updateTimePeriodDates,
  updateRollingAverageEnabled,
  updateRollingAverageWindowSize,
  updateViewerOptions,
  updateVisualizationBase,
  updateStatTestingEnabled,
  updateStatTestType,
  updateSignificanceLevel,
  updateMinimumSampleSize,
  updateCompareToTotal,
  updateReferenceColumnEntity,
  toggleApplyFilters,
  setApplyFilters,
  updateSortingOption,
  updateColorCodingTemplate,
  addCategoryRollup,
  updateCategoryRollup,
  deleteCategoryRollup,
  clearCategoryRollups,
  mergeVariableIdLabelMap,
  addCrossCategoryRollup,
  updateCrossCategoryRollup,
  deleteCrossCategoryRollup,
  clearCrossCategoryRollups,
  mergeCrossVariableIdLabelMap,
} = crosstabConfigSlice.actions;

export const crosstabConfigDispatcher = createDispatchers(
  crosstabConfigSlice.actions,
);
export const crosstabConfigSelector = (state: RootState) =>
  state.crosstabConfig;

export const crosstabConfigReducer = crosstabConfigSlice.reducer;
